import React from 'react';
import PropTypes from 'prop-types';
import EmptyState from './EmptyState';
import { CHART_COLORS } from '../utils/chartStyles';
import ChartDescription from './ChartDescription';

// Shared horizontal bar chart that replaces every pie chart (spec FR-011 to FR-013).
//  - rows sorted by count, highest first; the top `topN` (default 8) are shown, the rest are
//    grouped into one "Other" row whose hover text lists the grouped items;
//  - every row shows its count and its percentage of the total;
//  - a row is a real <button> when `onBarClick` is given, so it works with keyboard (Tab, Enter,
//    Space) and screen readers; "Other" is never clickable;
//  - the chart has a text alternative (aria-label) that lists the top categories.
// It is drawn with plain HTML and CSS rather than recharts, which gives keyboard operation and a
// text alternative without extra code and keeps the layout identical in light and dark themes.

export const OTHER_KEY = '__other__';

// Converts the {name, value, rawValue?, submitted?} items the tabs already build for their charts
// into rows for ShareBarChart. rawValue (when present) is the code the drill-down needs.
export const toShareData = items => (items || []).map(item => ({
  key: item.rawValue !== undefined ? item.rawValue : item.name,
  label: String(item.name),
  value: item.value,
  submitted: item.submitted
}));

const formatCount = value => Number(value || 0).toLocaleString('en-US');

export const formatPercent = value => `${(Math.round(value * 10) / 10).toFixed(1)}%`;

export const buildRows = (data, topN = 8, otherLabel = 'Other') => {
  const clean = (data || [])
    .map(item => ({ ...item, value: Number(item.value) || 0 }))
    .filter(item => item.value > 0)
    .sort((a, b) => b.value - a.value || String(a.label).localeCompare(String(b.label)));
  const total = clean.reduce((sum, item) => sum + item.value, 0);

  let rows = clean;
  if (clean.length > topN) {
    const rest = clean.slice(topN);
    rows = [
      ...clean.slice(0, topN),
      {
        key: OTHER_KEY,
        label: otherLabel,
        value: rest.reduce((sum, item) => sum + item.value, 0),
        submitted: rest.reduce((sum, item) => sum + (Number(item.submitted) || 0), 0),
        isOther: true,
        grouped: rest
      }
    ];
  }

  return {
    total,
    rows: rows.map(row => ({ ...row, percent: total > 0 ? (row.value / total) * 100 : 0 }))
  };
};

const rowHoverText = row => {
  if (!row.isOther) return `${row.label}: ${formatCount(row.value)} (${formatPercent(row.percent)})`;
  const list = row.grouped.map(item => `${item.label} ${formatCount(item.value)}`).join(', ');
  return `${row.label}: ${formatCount(row.value)} (${formatPercent(row.percent)}) — ${list}`;
};

const ShareBarChart = ({
  data, title, descriptionKey, onBarClick, topN, showSubmitted, otherLabel, emptyMessage, colors
}) => {
  const { total, rows } = buildRows(data, topN, otherLabel);

  if (rows.length === 0) {
    return (
      <div data-testid="share-bar-chart-empty">
        <EmptyState title={emptyMessage} />
      </div>
    );
  }

  const max = Math.max(...rows.map(row => row.value), 1);
  const summary = rows
    .slice(0, 3)
    .map(row => `${row.label} ${formatPercent(row.percent)}`)
    .join(', ');
  const ariaLabel = `${title || 'Chart'}: ${rows.length} categories, ${formatCount(total)} in total. Top: ${summary}.`;

  return (
    <div role="group" aria-label={ariaLabel} data-testid="share-bar-chart">
      {descriptionKey && <ChartDescription descriptionKey={descriptionKey} />}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {rows.map((row, index) => {
          const color = colors[index % colors.length];
          const clickable = Boolean(onBarClick) && !row.isOther;
          const content = (
            <>
              <span
                style={{
                  flex: '0 0 34%', textAlign: 'left', fontSize: '0.875rem', overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}
              >
                {row.label}
              </span>
              <span style={{ flex: '1 1 auto', background: '#f0f0f0', borderRadius: 4, height: 18 }}>
                <span
                  data-testid="share-bar-fill"
                  style={{
                    display: 'block', height: '100%', borderRadius: 4, background: color,
                    width: `${(row.value / max) * 100}%`
                  }}
                />
              </span>
              <span style={{ flex: '0 0 24%', textAlign: 'right', fontSize: '0.875rem' }}>
                {formatCount(row.value)} ({formatPercent(row.percent)})
                {showSubmitted && row.submitted !== undefined && (
                  <span style={{ display: 'block', fontSize: '0.75rem', color: '#666666' }}>
                    {formatCount(row.submitted)} submitted
                  </span>
                )}
              </span>
            </>
          );
          const rowStyle = {
            display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', padding: '0.4rem 0.25rem',
            background: 'transparent', border: 0, font: 'inherit', color: 'inherit'
          };
          return (
            <li key={row.key} style={{ margin: 0 }}>
              {clickable ? (
                <button
                  type="button"
                  title={rowHoverText(row)}
                  aria-label={`${rowHoverText(row)}. Show the records.`}
                  onClick={() => onBarClick(row.key)}
                  style={{ ...rowStyle, cursor: 'pointer' }}
                >
                  {content}
                </button>
              ) : (
                <div title={rowHoverText(row)} style={rowStyle} data-testid={row.isOther ? 'share-bar-other' : undefined}>
                  {content}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

ShareBarChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    submitted: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })),
  title: PropTypes.string,
  descriptionKey: PropTypes.string,
  onBarClick: PropTypes.func,
  topN: PropTypes.number,
  showSubmitted: PropTypes.bool,
  otherLabel: PropTypes.string,
  emptyMessage: PropTypes.string,
  colors: PropTypes.arrayOf(PropTypes.string)
};

ShareBarChart.defaultProps = {
  data: [],
  title: '',
  descriptionKey: null,
  onBarClick: null,
  topN: 8,
  showSubmitted: false,
  otherLabel: 'Other',
  emptyMessage: 'No data for this period',
  colors: CHART_COLORS
};

export default ShareBarChart;
